// Copyright © 2026 Alex Bruce. All rights reserved.
// Proprietary and confidential.

/**
 * Rulers down the edges of the sheet.
 *
 * A drawing on screen has no size. You can see that a trailer is near a
 * property line, but not whether you are looking at a letter-size print or
 * an ARCH D sheet — and that decides whether the scale written in the title
 * block survives the printer. A ruler says it without anyone asking: the
 * sheet is as many inches across as the ruler counts.
 *
 * Inches, not feet. The feet are on the drawing already, in the dimensions
 * and the graphic scale; what is missing is the paper.
 */

/** Tick spacings worth using, in inches. */
export const STEPS = [0.125, 0.25, 0.5, 1, 2, 5, 10, 20, 50];

/** How close two ticks may be before they stop being readable. */
export const MIN_TICK_PX = 6;
/** And how far apart a labelled one should be. Wide enough that a sheet at
 *  1:1 is numbered in whole inches, which is how a ruler reads. */
export const MIN_LABEL_PX = 60;

export interface Tick {
  /** Distance from the start of the ruler, in screen pixels. */
  px: number;
  /** Inches from the sheet's edge. */
  inch: number;
  /** Long tick with a number beside it. */
  major: boolean;
}

/**
 * The step to tick at, given how much room an inch has on screen.
 *
 * Chosen from a ladder rather than computed, because a ruler marked every
 * 0.37 inches is not a ruler. The smallest step whose ticks are still
 * separable wins, so the ruler gets finer as you zoom in.
 */
export function tickStep(pxPerInch: number, minPx = MIN_TICK_PX): number {
  if (!Number.isFinite(pxPerInch) || pxPerInch <= 0) return STEPS[STEPS.length - 1];
  for (const s of STEPS) if (s * pxPerInch >= minPx) return s;
  return STEPS[STEPS.length - 1];
}

/** How often to put a number on, in inches. */
export function labelStep(pxPerInch: number, step: number, minPx = MIN_LABEL_PX): number {
  if (!Number.isFinite(pxPerInch) || pxPerInch <= 0) return step;
  let s = step;
  for (const candidate of STEPS) {
    if (candidate < step) continue;
    s = candidate;
    if (candidate * pxPerInch >= minPx) break;
  }
  return s;
}

/**
 * Every tick along a ruler.
 *
 * Measured from the sheet's own edge, not the window's, so zero is the
 * corner of the paper wherever that has been scrolled or zoomed to.
 */
export function ticksFor(
  sheetInches: number, pxPerInch: number, originPx = 0,
): Tick[] {
  if (!Number.isFinite(sheetInches) || sheetInches <= 0) return [];
  if (!Number.isFinite(pxPerInch) || pxPerInch <= 0) return [];
  const step = tickStep(pxPerInch);
  const label = labelStep(pxPerInch, step);
  const out: Tick[] = [];
  // Counted in steps rather than accumulated, so the last tick is where it
  // should be instead of a few thousandths out.
  const n = Math.floor(sheetInches / step + 1e-9);
  for (let i = 0; i <= n; i++) {
    const inch = i * step;
    // Whether this tick is a labelled one, decided in whole steps to avoid
    // a modulo on floating point missing every other label.
    const major = Math.abs(Math.round(inch / label) * label - inch) < 1e-9;
    out.push({ px: originPx + inch * pxPerInch, inch, major });
  }
  return out;
}

/** A tick's number as it reads on the ruler. */
export function tickLabel(inch: number): string {
  if (Math.abs(inch - Math.round(inch)) < 1e-9) return String(Math.round(inch));
  return String(Number(inch.toFixed(3)));
}

/**
 * The sheet's size as a sentence.
 *
 * Named where it matches a standard sheet, because "ARCH D" is what people
 * say, and plain inches where it does not — a size that is not a standard
 * one is worth seeing rather than being rounded into the nearest name.
 */
export const SHEET_SIZES: Array<{ name: string; w: number; h: number }> = [
  { name: "Letter", w: 11, h: 8.5 },
  { name: "Tabloid", w: 17, h: 11 },
  { name: "ARCH A", w: 12, h: 9 },
  { name: "ARCH B", w: 18, h: 12 },
  { name: "ARCH C", w: 24, h: 18 },
  { name: "ARCH D", w: 36, h: 24 },
  { name: "ARCH E", w: 48, h: 36 },
  { name: "ARCH E1", w: 42, h: 30 },
  { name: "ANSI C", w: 22, h: 17 },
  { name: "ANSI D", w: 34, h: 22 },
  { name: "ANSI E", w: 44, h: 34 },
];

export function sheetSizeName(w: number, h: number, tol = 0.15): string | null {
  if (!Number.isFinite(w) || !Number.isFinite(h) || w <= 0 || h <= 0) return null;
  for (const s of SHEET_SIZES) {
    const fits = (a: number, b: number) => Math.abs(a - b) <= tol;
    if ((fits(w, s.w) && fits(h, s.h)) || (fits(w, s.h) && fits(h, s.w))) return s.name;
  }
  return null;
}

/** "36 × 24 in (ARCH D)", or just the inches when it is not a named size. */
export function describeSheet(w: number, h: number): string {
  if (!Number.isFinite(w) || !Number.isFinite(h) || w <= 0 || h <= 0) return "";
  const round = (v: number) => (Math.abs(v - Math.round(v)) < 0.05
    ? String(Math.round(v)) : v.toFixed(2).replace(/0$/, ""));
  const name = sheetSizeName(w, h);
  return `${round(w)} × ${round(h)} in${name ? ` (${name})` : ""}`;
}
